import InputLabel from '@mui/material/InputLabel';
import Stack from '@mui/material/Stack';
import React, { memo } from 'react';
import { Controller } from 'react-hook-form';
import PhoneNumberInput from '../Input/PhoneNumberInput';

/**
 * ControlledTextField is a wrapper around the Controller component from react-hook-form.
 * It is used to control the input fields in the form.
 * @param {object} props
 * @param {string} props.label - The label for the input field
 * @param {string} props.name - The name of the input field
 * @param { import('react-hook-form').Control<any, any>} props.control - The control object from react-hook-form
 * @param {string} props.placeholder - The placeholder for the input field
 * @param {boolean} props.error - The error state of the input field
 * @param {string} props.helperText - The helper text for the input field
 * @param {boolean} props.fullWidth - The full width state of the input field
 * @param {boolean} props.required - The required state of the input field
 * @param {Omit<import('react-hook-form').UseControllerProps, 'name'>} props.controllerOptions - The options for the controller
 * @param {import('@mui/material/InputLabel').InputLabelProps} props.inputLabelProps - The props for the input label
 * @param {import('@mui/material/Input').InputProps} props.inputProps - The props for the input label
 * @param {string} [props.defaultCountry] - ISO code of the initially selected country, see PhoneNumberInput
 * @param {boolean} [props.prefillDialCode] - prefill the blank field with the dial code, see PhoneNumberInput
 *
 * @returns {React.JSX.Element}
 */

const ControlledPhoneNumberInputComponent = ({
  control,
  controllerOptions = {},
  defaultCountry = 'DE',
  error = null,
  fullWidth = false,
  helperText = '',
  inputLabelProps = {},
  inputProps = {},
  label = '',
  name,
  placeholder = '',
  prefillDialCode = false,
  required = false,
}) => {
  if (!name) {
    throw new Error('ControlledTextField: name is required');
  }
  if (!(controllerOptions?.control || control)) {
    throw new Error('ControlledTextField: control is required');
  }

  const id = `id_${name}`;
  inputLabelProps.htmlFor = id;
  inputProps.id = id;

  return (
    <Controller
      control={control}
      name={name}
      {...controllerOptions}
      render={({ field, fieldState, formState }) => (
        <Stack spacing={1} width="100%">
          {label ? (
            <InputLabel htmlFor={id} required={required} shrink={false} {...inputLabelProps}>
              {label}
            </InputLabel>
          ) : null}
          <PhoneNumberInput
            defaultCountry={defaultCountry}
            error={error}
            field={field}
            fullWidth={fullWidth}
            helperText={helperText}
            id={id}
            inputProps={inputProps}
            name={name}
            placeholder={placeholder}
            prefillDialCode={prefillDialCode}
            required={required}
            size="small"
          />
        </Stack>
      )}
    />
  );
};

ControlledPhoneNumberInputComponent.displayName = 'ControlledPhoneNumberInput';

const ControlledPhoneNumberInput = memo(ControlledPhoneNumberInputComponent);

export default ControlledPhoneNumberInput;
